import { useState } from 'react';
import { P } from '@stockpilot/shared';
import { useCreateExpenseCategoryMutation, type ExpenseCategory } from '@/api/endpoints';
import { apiFieldErrors } from '@/api/baseApi';
import { Input } from '@/components/ui';
import { useAccess } from '@/hooks/useAccess';
import { CreatableSelect } from '@/components/CreatableSelect';

const emptyCategory = { name: '', description: '' };

interface ExpenseCategoryPickerProps {
  value: string;
  onChange: (categoryId: string) => void;
  categories: ExpenseCategory[];
  hint?: string;
  error?: string;
  required?: boolean;
}

/**
 * An expense-category dropdown that can create the category it is missing.
 *
 * A business meets its own categories one at a time — the first rent payment,
 * the first fuel receipt — and an expense cannot be recorded without one. With
 * no way to add a category from here, a business with none is simply stuck.
 */
export function ExpenseCategoryPicker({
  value,
  onChange,
  categories,
  hint,
  error,
  required,
}: ExpenseCategoryPickerProps) {
  const { can } = useAccess();
  const [createCategory, createState] = useCreateExpenseCategoryMutation();
  const [form, setForm] = useState(emptyCategory);

  const fieldErrors = apiFieldErrors(createState.error);

  return (
    <CreatableSelect
      label="Category"
      noun="category"
      required={required}
      value={value}
      onChange={onChange}
      hint={hint}
      error={error}
      placeholder="Select a category"
      options={categories.map((category) => ({ id: category.id, label: category.name }))}
      canCreate={can(P.EXPENSE_CREATE)}
      creating={createState.isLoading}
      createDisabled={!form.name}
      onReset={() => setForm(emptyCategory)}
      onCreate={async () => {
        const created = await createCategory({
          name: form.name,
          description: form.description || undefined,
        }).unwrap();
        return created.id;
      }}
      form={
        <div className="grid gap-3 sm:grid-cols-2">
          <Input
            label="Name"
            placeholder="Rent"
            value={form.name}
            onChange={(event) => setForm({ ...form, name: event.target.value })}
            error={fieldErrors.name}
          />
          <Input
            label="Description"
            placeholder="Premises and storage"
            value={form.description}
            onChange={(event) => setForm({ ...form, description: event.target.value })}
          />
        </div>
      }
    />
  );
}
